/**
 * components/branch/BranchPicker.js
 *
 * The navbar's "Branch" choice (4 Oct 2026). It sets the active location in
 * context/BranchScopeContext.js, which the routed views are keyed on, so the
 * orders, sales, kitchen and dashboards refetch for the branch picked.
 *
 * Hidden for a vendor with no branches and for platform staff. A member who
 * works at a single location sees its name, not a choice. A member whose
 * branches have all been deleted or taken away sees "No branch" with the
 * reason, so their empty screens are explained.
 */
import React from "react";
import { AppSelect } from "../ui";
import { useBranchScope } from "../../context/BranchScopeContext.js";
import { ALL } from "../../utilities/vendorScope.js";
import "./branch.css";

export const NO_BRANCH_TITLE =
  "Your access does not cover any branch that is open on Lusuku, so there is nothing to show. Ask the owner of the business to give you a branch.";

export default function BranchPicker() {
  const { hasBranches, locations, active, setActive, locked, isBranchScoped, activeLocation, noBranchAccess } =
    useBranchScope();
  if (noBranchAccess) {
    return (
      <div className="lk-branch-picker" title={NO_BRANCH_TITLE}>
        <span className="lk-branch-picker__label">Branch</span>
        <span className="lk-pill lk-branch-chip lk-branch-chip--warn">No branch</span>
      </div>
    );
  }
  if (!hasBranches) return null;

  if (locked) {
    return (
      <div className="lk-branch-picker" title="Your access covers this branch only">
        <span className="lk-branch-picker__label">Branch</span>
        <span className="lk-branch-picker__locked">{activeLocation?.name || locations[0]?.name}</span>
      </div>
    );
  }

  return (
    <div className="lk-branch-picker">
      <label className="lk-branch-picker__label" htmlFor="navbar-branch-picker">Branch</label>
      <AppSelect
        id="navbar-branch-picker"
        size="sm"
        value={active}
        onChange={(e) => setActive(e.target.value)}
        style={{ minWidth: 180 }}
        aria-label="Which branch to show"
      >
        <option value={ALL}>{isBranchScoped ? "All my branches" : "All branches"}</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.isActive === false ? `${l.name} (closed)` : l.name}
          </option>
        ))}
      </AppSelect>
    </div>
  );
}
